import { redirectModalVisibleOrLoadingAtom } from "@mirage/eligibility/redirectModalAtoms";
import { useShouldShowWelcomeModal } from "@mirage/growth/onboarding/hooks/useShouldShowWelcomeModal";
import { useAppsTooltip } from "@mirage/mosaics/GlobalNav/useAppsTooltip";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useAddAppsModal } from "../hooks/useAddAppsModal";
import { AddAppsModal } from "./AddAppsModal";
import WelcomeModal from "./WelcomeModal";

const WELCOME_MODAL_VERSION = 2;

export default function FssPreviewModals() {
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [fullPageRedirectDisplayed] = useAtom(
    redirectModalVisibleOrLoadingAtom,
  );

  const featureFlagEnabled = useFeatureFlagValueBool(
    "otc_growth_2025_06_23_fss_preview_welcome_modal",
  );

  const {
    shouldShowModal: shouldShowWelcomeModal,
    setDismissed: setWelcomeModalDismissed,
    dismissed: welcomeModalDismissed,
    initialized: welcomeModalInitialized,
  } = useShouldShowWelcomeModal(WELCOME_MODAL_VERSION);
  const {
    shouldDisplay: shouldShowAddAppsModal,
    dismissed: addAppsModalDismissed,
    hideAddAppsModal,
    showAddAppsModal,
  } = useAddAppsModal();
  const {
    dismissed: addAppsTooltipDismissed,
    openTooltip: openAddAppsTooltip,
  } = useAppsTooltip();

  useEffect(() => {
    if (shouldShowWelcomeModal && !welcomeModalDismissed) {
      setWelcomeModalOpen(true);
    }

    if (!addAppsModalDismissed && shouldShowAddAppsModal) {
      showAddAppsModal();
    }
  }, [
    addAppsModalDismissed,
    shouldShowAddAppsModal,
    shouldShowWelcomeModal,
    showAddAppsModal,
    welcomeModalDismissed,
    welcomeModalInitialized,
  ]);

  const onCloseWelcomeModal = useCallback(
    (isAddApps: boolean) => {
      setWelcomeModalDismissed();
      setWelcomeModalOpen(false);

      if (shouldShowAddAppsModal || isAddApps) {
        showAddAppsModal();
      }
    },
    [setWelcomeModalDismissed, shouldShowAddAppsModal, showAddAppsModal],
  );

  const onCloseAddAppsModal = useCallback(() => {
    hideAddAppsModal();
    if (!addAppsTooltipDismissed) {
      // If the user has not dismissed the post-Add Apps tooltip, we want to show it
      openAddAppsTooltip();
    }
  }, [addAppsTooltipDismissed, hideAddAppsModal, openAddAppsTooltip]);

  if (!featureFlagEnabled || fullPageRedirectDisplayed) return null;

  if (welcomeModalOpen) {
    return (
      <WelcomeModal
        open={welcomeModalOpen}
        onClose={onCloseWelcomeModal}
        variant={shouldShowAddAppsModal ? "addApps" : "default"}
      />
    );
  }

  if (shouldShowAddAppsModal && welcomeModalInitialized) {
    return (
      <AddAppsModal
        open={shouldShowAddAppsModal}
        onClose={onCloseAddAppsModal}
      />
    );
  }

  return null;
}
